import { cx } from "@linaria/core";
import { useMemo } from "react";

import { textComponentHtmlParsers } from "@pm2/content-engine";
import { textComponentChildPrinters } from "@pm2/text-component";
import {
  containerCls,
  headingsCls,
  spacingCls,
} from "@pm2/text-component/selectors";
import { ReliefSVG } from "@pm2/ui-components";
import { breakpoints } from "@ui/css";
import { Tooltip } from "@ui/kit";
import { CopyrightIcon } from "@ui/kit/icons";
import { printAST, xml2ast } from "@utils/ast";
import { HideFor, PostloadedImage, ShowFor } from "@utils/react";

import * as styles from "./page.content.frontpage-topsection.style";

type Props = {
  label?: string;
  heading: string;
  description: string;

  withBackground?: boolean;

  image: {
    src: string;
    width?: number;
    height?: number;
    creditLine?: string;
    alt?: string | null;
  };
};

export function FrontpageTopsection(props: Props) {
  const content = useMemo(
    () =>
      printAST(
        xml2ast(props.description, textComponentHtmlParsers, "text/html"),
        textComponentChildPrinters,
      ),
    [props.description],
  );

  return (
    <section
      className={styles.section}
      data-frontpage-top-section
      data-with-background={props.withBackground}
    >
      <div className={styles.content}>
        {props.label && <div className={styles.label}>{props.label}</div>}
        <h1 className={styles.heading}>{props.heading}</h1>

        <HideFor breakpoint={breakpoints.mobile}>
          <div
            className={cx(styles.body, containerCls, spacingCls, headingsCls)}
          >
            {content}
          </div>
        </HideFor>
      </div>

      <div className={styles.imageWrapper}>
        <PostloadedImage
          className={styles.image}
          src={props.image.src}
          sizes="(max-width: 768px) 100vw, 550px"
          width={props.image.width}
          height={props.image.height}
          alt={props.image.alt ?? ""}
        />

        {props.image.creditLine && (
          <Tooltip
            ariaType="label"
            placement="left"
            body={`© ${props.image.creditLine}`}
            showAfterTapMs={5000}
          >
            {(ref) => (
              <div ref={ref} className={styles.creditLine}>
                <CopyrightIcon />
              </div>
            )}
          </Tooltip>
        )}
      </div>

      {props.withBackground && (
        <PostloadedImage
          role="presentation"
          className={styles.backdrop}
          src={props.image.src}
          sizes="100vw"
          width={props.image.width}
          height={props.image.height}
          alt=""
        />
      )}

      <ShowFor breakpoint={breakpoints.mobile} className={styles.mobileContent}>
        <div className={cx(styles.body, containerCls, spacingCls, headingsCls)}>
          {content}
        </div>
      </ShowFor>

      {props.withBackground && (
        <ReliefSVG position="top" className={styles.graphic} />
      )}
    </section>
  );
}
